import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import type {MilestoneState} from '@github-ui/server-persisted-queries/types/RepositoryMilestoneIndexPageQuery'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useNavigate} from '@github-ui/use-navigate'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, ConfirmationDialog} from '@primer/react'
import {AriaAlert} from '@primer/react/experimental'
import {type RefObject, useCallback, useEffect, useRef, useState} from 'react'

import {LABELS} from './constants/labels'
import {useDeleteMilestoneMutation} from './mutations/use-delete-milestone'
import type {MilestoneNode} from './repository-milestone-index-page-query-types'
import styles from './RepositoryMilestone.module.css'
import {useToggleMilestoneStateMutation} from './use-toggle-milestone-state'

export type MilestoneRowMenuFragment = Pick<MilestoneNode, 'id' | 'state' | 'url' | 'repository'>

type MilestoneRowMenuProps = {
  milestone: MilestoneRowMenuFragment
  queryKey?: RouteQueryKey
  listFocusRef?: RefObject<HTMLDivElement | null>
  onMutationSuccess?: (milestoneNumber: number) => void | Promise<void>
  /** Overrides onMutationSuccess for deletes when the caller needs to evict removed entities. */
  onDeleteSuccess?: (milestoneNumber: number) => void | Promise<void>
}

/**
 * Extracts the milestone number from a milestone URL.
 * URL format: github.com/owner/repo/milestone/3 or https://github.com/owner/repo/milestone/3
 */
function extractMilestoneNumber(url: string): number {
  const parts = url.split('/')
  const milestoneIndex = parts.lastIndexOf('milestone')
  return Number(parts[milestoneIndex + 1])
}

/**
 * Extracts nameWithOwner (owner/repo) from a milestone URL.
 * URL format: github.com/owner/repo/milestone/3 or https://github.com/owner/repo/milestone/3
 */
function extractNameWithOwner(url: string): string {
  const parts = url.split('/')
  const milestoneIndex = parts.lastIndexOf('milestone')
  return `${parts[milestoneIndex - 2]}/${parts[milestoneIndex - 1]}`
}

export function MilestoneRowMenu({
  milestone: data,
  queryKey,
  listFocusRef,
  onMutationSuccess,
  onDeleteSuccess,
}: MilestoneRowMenuProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorsPresent, setErrorsPresent] = useState(false)

  const errorBanner = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()

  const {addToast} = useToastContext()

  const showToggleMilestoneErrorToast = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({
      type: 'error',
      message: 'Could not change Milestone state',
    })
  }, [addToast])

  const repositoryId = data.repository.id
  const milestoneId = data.id
  const milestoneNumber = extractMilestoneNumber(data.url)
  const nameWithOwner = extractNameWithOwner(data.url)
  const milestoneState: MilestoneState = data.state

  const toggleMilestone = useToggleMilestoneStateMutation({
    repositoryId,
    milestoneId,
    milestoneNumber,
    nameWithOwner,
    fromState: milestoneState,
    queryKey,
    onError: showToggleMilestoneErrorToast,
    onSuccess: () => onMutationSuccess?.(milestoneNumber),
  })

  const deleteMilestone = useDeleteMilestoneMutation({
    repositoryId,
    milestoneId,
    milestoneNumber,
    nameWithOwner,
    state: milestoneState,
    queryKey,
    onSuccess: async () => {
      setIsDeleteDialogOpen(false)
      setIsProcessing(false)
      setErrorsPresent(false)
      // With the fold active (queryKey), the row + its ⋯ trigger unmount; move focus to the surviving
      // list container (rAF beats the dialog's focus-return) so it doesn't fall to <body>. WCAG 2.4.3.
      if (queryKey) {
        requestAnimationFrame(() => listFocusRef?.current?.focus({preventScroll: true}))
      }
      await (onDeleteSuccess ?? onMutationSuccess)?.(milestoneNumber)
    },
    onError: () => {
      setErrorsPresent(true)
      setIsProcessing(false)
    },
  })

  const editCallback = useCallback(() => {
    const href = `${data.url.replace('/milestone/', '/milestones/')}/edit`
    const hrefParts = href.split('/')
    const milestoneIndex = hrefParts.lastIndexOf('milestone')
    if (milestoneIndex !== -1 && hrefParts[milestoneIndex + 1] === '') {
      hrefParts[milestoneIndex] = 'milestones'
    }
    const correctedHref = hrefParts.join('/')
    const url = new URL(correctedHref, window.location.origin)
    const path = url.pathname
    navigate(path)
  }, [data.url, navigate])

  const closeCallback = useCallback(() => {
    toggleMilestone()
    // The overlay unmounts the row immediately; move focus to the list container, as in delete. WCAG 2.4.3.I
    if (queryKey) {
      requestAnimationFrame(() => listFocusRef?.current?.focus({preventScroll: true}))
    }
  }, [toggleMilestone, queryKey, listFocusRef])

  const deleteCallback = useCallback(() => {
    if (isProcessing) return

    setIsProcessing(true)
    deleteMilestone()
  }, [isProcessing, deleteMilestone])

  // Prevent Enter action from bubbling up and redirecting to milestone show
  const handleMenuButtonKeyDown = useCallback((event: React.KeyboardEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === 'Enter') {
      event.stopPropagation()
    }
  }, [])

  useEffect(() => {
    if (errorsPresent && errorBanner?.current) {
      errorBanner.current.focus()
    }
  }, [errorBanner, errorsPresent])

  const {viewerCanDeleteMilestones, viewerCanUpdateMilestones} = data.repository

  if (!viewerCanUpdateMilestones && !viewerCanDeleteMilestones) {
    return null
  }

  return (
    <>
      <ActionMenu>
        <ActionMenu.Button
          className={styles.menuButton}
          aria-label="Milestone menu"
          variant="invisible"
          icon={<KebabHorizontalIcon />}
          onKeyDown={handleMenuButtonKeyDown}
        />

        <ActionMenu.Overlay>
          <ActionList>
            {viewerCanUpdateMilestones && (
              <>
                <ActionList.Item onSelect={() => editCallback()}>Edit</ActionList.Item>
                <ActionList.Item onSelect={() => closeCallback()}>
                  {data.state === 'CLOSED' ? 'Open' : 'Close'}
                </ActionList.Item>
              </>
            )}
            {viewerCanDeleteMilestones && (
              <>
                <ActionList.Divider />
                <ActionList.Item onSelect={() => setIsDeleteDialogOpen(true)}>
                  <span className={styles.deleteMilestoneButton}>Delete</span>
                </ActionList.Item>
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>

      {viewerCanDeleteMilestones && isDeleteDialogOpen && (
        <ConfirmationDialog
          onClose={gesture => {
            if (gesture === 'confirm') {
              deleteCallback()
            } else {
              setIsDeleteDialogOpen(false)
              setErrorsPresent(false)
            }
          }}
          title={<p className={styles.dialogTitle}>{LABELS.deleteMilestoneConfirmationTitle}</p>}
          cancelButtonContent={LABELS.cancel}
          confirmButtonContent={LABELS.deleteMilestoneConfirmationButton}
          confirmButtonType="danger"
        >
          <div className={styles.dialogDescriptionContainer}>
            {errorsPresent && (
              <Banner
                ref={errorBanner}
                className="tmp-mb-3"
                title="Error"
                description={<AriaAlert>{LABELS.deleteMilestoneError}</AriaAlert>}
                variant="critical"
                role="alert"
              />
            )}
            <p className={styles.dialogDescription}>{LABELS.deleteMilestoneWarningPermanent}</p>
            <p className={styles.dialogDescription}>{LABELS.deleteMilestoneAssociatedIssuesNote}</p>
          </div>
        </ConfirmationDialog>
      )}
    </>
  )
}
